/**
 * `agentIdentityStatus` — classify how confident Sill is that a visitor
 * really is who it says. The classifier is DETERMINISTIC, PURE, and
 * CONSERVATIVE: it only promotes an identity to "verified" when there
 * is a positive proof signal (a Web Bot Auth signature, or Cloudflare's
 * verified-bot network check), and only demotes to "likely impersonator"
 * when the caller claims a known bot name AND we have evidence it is
 * not that bot (probe traffic or a failed verification).
 *
 * The five statuses are a closed set so the renderer can colour and
 * phrase them without a prop drill:
 *
 *   - verified_signature  — Web Bot Auth signature verified (RFC-9421)
 *   - verified_network    — Cloudflare's verified-bot network recognised
 *                           this identity (one or more hits in-window)
 *   - likely_impersonator — Claims a known bot name AND (probe traffic
 *                           OR a failed verification signal). The amber
 *                           case the Journey drawer and Agent detail
 *                           lead with.
 *   - unverified          — Claims a known bot name but no verification
 *                           is available. Shown quietly next to the
 *                           name as "Known agent · unverified".
 *   - unknown             — Nothing to say — e.g. a human browser, an
 *                           unregistered identity with no claim to be a
 *                           known bot and no probe traffic. Renderers
 *                           hide the chip entirely.
 *
 * The classifier takes a minimal input shape derived from the
 * `agent_daily_rollup` row + the overview response's `funnel.by_agent`
 * fields. It does NOT consult registry policy tables or do I/O — the
 * caller passes `has_known_bot_name` (resolved from the registry) and
 * the raw signal counts.
 *
 * Spec reference: CLAUDE.md §3 (ed25519 signing posture; signing proof
 * via `packages/webbotauth`), ADR-0210 Slice 5 (overview response shape),
 * ADR-0128 (public JWKS as verification root for Sill-signed artifacts).
 */
/**
 * ADR-0229 — sum the `imp_*` keys the agent rollup writes into
 * `spoof_signals` (sig_invalid keeps its own key and its own input).
 */
export function impersonationHitsFromSignals(spoof_signals) {
    if (spoof_signals === null || spoof_signals === undefined)
        return 0;
    let n = 0;
    for (const [k, v] of Object.entries(spoof_signals)) {
        if (k.startsWith('imp_'))
            n += Number(v) || 0;
    }
    return n;
}
/**
 * Classify the identity. Returns one of five closed-set labels.
 */
export function agentIdentityStatus(input) {
    const known = input.has_known_bot_name === true;
    const signed = (input.signed_verified_hits ?? 0) > 0;
    const sig_invalid = (input.signature_invalid_hits ?? 0) > 0;
    const cf_verified = (input.cf_verified_hits ?? 0) > 0;
    const cf_unverified = (input.cf_unverified_hits ?? 0) > 0;
    const typo = (input.typo_ua_hits ?? 0) > 0;
    const probe = (input.probe_hits ?? 0) > 0;
    const impersonated = (input.impersonation_hits ?? 0) > 0;
    // Impersonator wins over verification when there is probe traffic
    // or a signature-invalid signal AND the caller claims a known name.
    // A single valid signature doesn't absolve a UA that spent 48 hits
    // asking for /.env.
    if (known && (probe || typo || sig_invalid))
        return 'likely_impersonator';
    // ADR-0229 — recorded impersonation evidence. Positive verification
    // still wins below only when there is NO such evidence: a verified hit
    // proves some traffic was real, not that this identity row is clean.
    if (impersonated && !(input.signed_verified_hits ?? 0))
        return 'likely_impersonator';
    // Positive verification signals.
    if (signed)
        return 'verified_signature';
    if (cf_verified)
        return 'verified_network';
    // Claims a known name but no verification + nothing damning.
    if (known) {
        // `cf_unverified` on a known name without probes / typos is
        // "unverified" rather than impersonator — Cloudflare couldn't
        // match the IP range, which is common for a real bot operating
        // outside its documented ranges.
        if (cf_unverified)
            return 'unverified';
        return 'unverified';
    }
    return 'unknown';
}
/**
 * One-line explainer for the "How we know" row under an agent in the
 * Journey drawer and Agent detail. Returns an empty string when the
 * status is `unknown` so the renderer can omit the row.
 */
export function agentIdentityStatusExplanation(status, name) {
    if (status === 'verified_signature') {
        return `Its requests carried a Web Bot Auth signature that matched the operator's published key.`;
    }
    if (status === 'verified_network') {
        return `Your Cloudflare install reported that Cloudflare recognised this operator's network.`;
    }
    if (status === 'likely_impersonator') {
        return `${name} claimed this identity but failed Sill's verification — probe traffic, a misspelled identity string, an invalid signature, or requests from outside the operator's published network.`;
    }
    if (status === 'unverified') {
        return `It uses a known name, but sent nothing Sill can check it against.`;
    }
    return '';
}
/**
 * Short label for the chip rendered next to the agent name. The
 * renderer colours "likely_impersonator" coral; everything else is
 * rendered as a quiet uppercase mono kicker.
 *
 * HONESTY: only `verified_signature` may use the word "Verified" — Sill
 * actually checked the signature. `verified_network` is reported by the
 * merchant's Cloudflare install (not something Sill verifies), so it is
 * labelled "Cloudflare-verified".
 */
export function agentIdentityStatusLabel(status) {
    if (status === 'verified_signature')
        return 'Verified · signature';
    if (status === 'verified_network')
        return 'Cloudflare-verified';
    if (status === 'likely_impersonator')
        return 'Likely impersonator';
    if (status === 'unverified')
        return 'Unverified';
    return '';
}
/** True when the inferred name looks like a known AI-bot name. The
 *  list is a short, hand-curated prefix set; the registry match
 *  (via `agent_id` in the input) is the more authoritative signal and
 *  overrides this. Kept broad enough to cover the names seen most in
 *  the wild (GPTBot, ChatGPT-User, ClaudeBot, PerplexityBot, Perplexity-
 *  User, Google-Extended, Googlebot, bingbot, Applebot). */
export function looksLikeKnownBotName(name) {
    if (typeof name !== 'string' || name.length === 0)
        return false;
    const lower = name.toLowerCase();
    const prefixes = [
        'gptbot',
        'chatgpt-user',
        'chatgpt',
        'claudebot',
        'claude-user',
        'claude',
        'perplexitybot',
        'perplexity-user',
        'perplexity',
        'google-extended',
        'googlebot',
        'googleother',
        'bingbot',
        'applebot',
        'cohere-ai',
        'anthropic',
        'oai-',
        'duckassistbot',
        'ccbot',
    ];
    for (const p of prefixes) {
        if (lower.startsWith(p))
            return true;
    }
    return false;
}
/**
 * Build an impersonation summary over a set of agent rows. Pure. The
 * `agent_id` → "has registry match" signal and the inferred-name
 * prefix match are the two "has_known_bot_name" sources; a row that
 * matches either path AND has probe / spoof traffic is an impersonator.
 */
export function impersonationSummary(by_agent) {
    let impersonators = 0;
    let failed_requests = 0;
    let probe_hits_total = 0;
    const abused_names = new Set();
    for (const row of by_agent) {
        const probe = Number(row.probe_hits ?? 0);
        probe_hits_total += probe;
        const sp = row.spoof_signals ?? {};
        const sig_invalid = Number(sp['sig_invalid'] ?? 0);
        const typo = Number(sp['typo_ua'] ?? 0);
        const cf_unverified = Number(sp['cf_unverified'] ?? 0);
        const has_registry = typeof row.agent_id === 'string' && row.agent_id.length > 0;
        const has_name = looksLikeKnownBotName(row.inferred_name);
        const known = has_registry || has_name;
        const status = agentIdentityStatus({
            has_known_bot_name: known,
            signed_verified_hits: Number(row.signed_verified_hits ?? 0),
            signature_invalid_hits: sig_invalid,
            cf_unverified_hits: cf_unverified,
            typo_ua_hits: typo,
            probe_hits: probe,
        });
        if (status === 'likely_impersonator') {
            impersonators += 1;
            failed_requests += probe + sig_invalid + typo + cf_unverified;
            if (typeof row.inferred_name === 'string' && row.inferred_name.length > 0) {
                abused_names.add(row.inferred_name.toLowerCase());
            }
            else if (has_registry && row.agent_id != null) {
                abused_names.add(row.agent_id.toLowerCase());
            }
        }
    }
    return {
        impersonators,
        failed_requests,
        probe_hits: probe_hits_total,
        distinct_names_abused: abused_names.size,
    };
}
//# sourceMappingURL=agent-identity-status.js.map